import { Link } from "react-router-dom";
import type { SessionRecap } from "../../trainer/recap.ts";
import { ModalDialog } from "../layout/ModalDialog.tsx";

export function SessionRecapModal({
  recap,
  title,
  onRepeatLine,
  onRecall,
  onBranch,
  onRepeatWeak,
  onClose,
  finalPlan,
}: {
  recap: SessionRecap;
  title: string;
  onRepeatLine: () => void;
  onRecall?: () => void;
  onBranch?: () => void;
  onRepeatWeak: () => void;
  onClose?: () => void;
  finalPlan?: string;
}) {
  return (
    <ModalDialog
      open
      onClose={onClose || onRepeatLine}
      labelledBy="summaryHeadline"
      className="help-modal summary-modal"
    >
      <article className="help-inner summary-inner">
        <header className="help-header summary-header">
          <div>
            <p className="eyebrow summary-eyebrow">
              {recap.review
                ? `Repeat positions · ${recap.review.total} ${recap.review.total === 1 ? "position" : "positions"}`
                : recap.freePlayMoves > 0
                  ? `You finished 15 moves and played ${recap.freePlayMoves} more`
                  : recap.movesPlayed < 15
                    ? `You played ${recap.movesPlayed} ${recap.movesPlayed === 1 ? "move" : "moves"}`
                    : "You finished 15 moves"}
            </p>
            <h2 id="summaryHeadline">{recap.headline}</h2>
            <p className="summary-opening" id="summaryOpening">
              {title}
            </p>
          </div>
          {onClose ? (
            <button className="help-close" type="button" aria-label="Close" onClick={onClose}>
              ×
            </button>
          ) : null}
        </header>
        <section className="help-section summary-body">
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-label">
                {recap.review || recap.practice ? "Recall evidence" : "Practice"}
              </span>
              <span className="stat-value">{recap.independentLabel}</span>
            </div>
            {recap.rankSummary ? (
              <div className="stat">
                <span className="stat-label">Average rank</span>
                <span className="stat-value">{recap.rankSummary}</span>
              </div>
            ) : null}
            {recap.review ? null : (
              <div className="stat">
                <span className="stat-label">First deviation</span>
                <span className="stat-value">
                  {recap.firstDeviation
                    ? `Move ${recap.firstDeviation.moveNumber} (${recap.firstDeviation.san})`
                    : "Stayed in theory"}
                </span>
              </div>
            )}
          </div>
          <div className="takeaway-rule">
            <p className="takeaway-label">Take this into your next session</p>
            <p className="takeaway-body">{recap.oneRule}</p>
          </div>
          {recap.cards.length > 0 ? (
            <div>
              <p className="takeaway-label">Five-move report cards</p>
              <ol className="recap-cards">
                {recap.cards.map((card) => (
                  <li key={card.title}>
                    <span className="recap-card-title">{card.title}</span>
                    <span className="recap-card-word">{card.word}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          {recap.themes && recap.themes.total > 0 ? (
            <p className="recap-themes">
              <strong>
                {recap.themes.done} of {recap.themes.total} course ideas reached the board.
              </strong>{" "}
              {recap.themes.missed.length > 0
                ? `Not available this game: ${recap.themes.missed.join(", ")}.`
                : recap.themes.done === recap.themes.total
                  ? "Every idea the course teaches appeared in your game."
                  : `Still ahead: ${Math.max(0, recap.themes.total - recap.themes.done)} ${recap.themes.total - recap.themes.done === 1 ? "idea" : "ideas"}.`}
            </p>
          ) : null}
          {recap.review && recap.review.missed.length > 0 ? (
            <div className="qblock qblock-drift">
              <p className="qblock-label">To repeat tomorrow</p>
              <ul className="qblock-body">
                {recap.review.missed.map((miss, index) => (
                  <li key={index}>
                    {miss.played
                      ? `You played ${miss.played}; the course move is ${miss.expected}.`
                      : `Marked to repeat; the course move is ${miss.expected}.`}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {finalPlan ? (
            <div className="takeaway-rule">
              <p className="takeaway-label">Your middlegame plan</p>
              <p className="takeaway-body">{finalPlan}</p>
            </div>
          ) : null}
          {recap.onePosition ? (
            <div className="qblock qblock-drift">
              <p className="qblock-label">One position to repeat</p>
              <p className="qblock-body">
                Move {recap.onePosition.moveNumber} ({recap.onePosition.san}):{" "}
                {recap.onePosition.note}
              </p>
            </div>
          ) : null}
        </section>
        <footer className="help-footer summary-footer">
          <div className="summary-actions">
            {onRecall ? (
              <button className="btn-primary" type="button" onClick={onRecall}>
                Try a short stretch from memory
              </button>
            ) : null}
            {onBranch ? (
              <button className="btn-ghost" type="button" onClick={onBranch}>
                Meet a different reply
              </button>
            ) : null}
            {onClose ? (
              <button className="btn-ghost" type="button" onClick={onClose}>
                Review final position
              </button>
            ) : null}
            <button
              className={onRecall ? "btn-ghost" : "btn-primary"}
              type="button"
              onClick={onRepeatLine}
            >
              Repeat line
            </button>
            <button
              className="btn-ghost"
              type="button"
              onClick={onRepeatWeak}
              disabled={!recap.onePosition}
            >
              Repeat weak
            </button>
            <Link className="btn-ghost" to="/openings">
              Try a different opening
            </Link>
          </div>
        </footer>
      </article>
    </ModalDialog>
  );
}
